import * as React from "react";
import { motion, LazyMotion, domAnimation } from "framer-motion";
import { cn } from "@/lib/utils";

const ICON_VARIANTS = {
  left: {
    initial: { scale: 0.8, opacity: 0, x: 0, y: 0, rotate: 0 },
    animate: { scale: 1, opacity: 1, rotate: -6, transition: { duration: 0.4, delay: 0.1 } },
    hover: { x: -22, y: -5, rotate: -15, scale: 1.1, transition: { duration: 0.2 } },
  },
  center: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, delay: 0.2 } },
    hover: { y: -10, scale: 1.15, transition: { duration: 0.2 } },
  },
  right: {
    initial: { scale: 0.8, opacity: 0, x: 0, y: 0, rotate: 0 },
    animate: { scale: 1, opacity: 1, rotate: 6, transition: { duration: 0.4, delay: 0.3 } },
    hover: { x: 22, y: -5, rotate: 15, scale: 1.1, transition: { duration: 0.2 } },
  },
} as const;

const CONTENT_VARIANTS = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.4, delay: 0.2 } },
};

const BUTTON_VARIANTS = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.4, delay: 0.35 } },
};

type Variant = "default" | "subtle" | "error";
type Size = "sm" | "default" | "lg";

export type EmptyStateAction = {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: React.ReactNode;
};

export type EmptyStateProps = {
  title: string;
  description?: string;
  /** 1 ícone (centralizado) ou 3 ícones (esquerda, centro, direita). */
  icons?: React.ReactNode[];
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  variant?: Variant;
  size?: Size;
  className?: string;
};

const SIZE_PADDING: Record<Size, string> = {
  sm: "p-6",
  default: "p-8 md:p-10",
  lg: "p-12 md:p-16",
};

const TITLE_SIZE: Record<Size, string> = {
  sm: "text-base",
  default: "text-lg md:text-xl",
  lg: "text-xl md:text-2xl",
};

const DESC_SIZE: Record<Size, string> = {
  sm: "text-xs",
  default: "text-sm",
  lg: "text-base",
};

function variantClasses(v: Variant) {
  if (v === "error")
    return "border border-destructive/40 bg-destructive/5 hover:bg-destructive/10";
  if (v === "subtle")
    return "border border-transparent bg-surface/30 hover:bg-surface/50";
  return "border border-dashed border-[color:var(--hairline)] bg-surface/30 hover:border-foreground/30 hover:bg-surface/50";
}

function ActionButton({
  action,
  primary,
}: {
  action: EmptyStateAction;
  primary?: boolean;
}) {
  const cls = cn(
    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
    primary
      ? "bg-foreground text-background hover:bg-foreground/90 shadow-sm hover:shadow"
      : "border border-[color:var(--hairline)] bg-surface text-foreground hover:bg-surface/80",
  );
  if (action.href) {
    return (
      <a href={action.href} className={cls}>
        {action.icon}
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={cls}>
      {action.icon}
      {action.label}
    </button>
  );
}

const IconChip = ({
  children,
  variant,
  className,
}: {
  children: React.ReactNode;
  variant: keyof typeof ICON_VARIANTS;
  className?: string;
}) => (
  <motion.div
    variants={ICON_VARIANTS[variant]}
    className={cn(
      "relative grid h-12 w-12 place-items-center rounded-xl border border-[color:var(--hairline)] bg-surface/80 text-foreground shadow-sm backdrop-blur-sm [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-muted-foreground",
      className,
    )}
  >
    {children}
  </motion.div>
);

/**
 * EmptyState padronizado (tema escuro).
 * - 1 ícone: aparece centralizado e flutua no hover.
 * - 3 ícones: layout em leque (esq/centro/dir) com microanimações.
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      title,
      description,
      icons,
      action,
      secondaryAction,
      variant = "default",
      size = "default",
      className,
    },
    ref,
  ) => {
    const hasIcons = icons && icons.length > 0;
    const isTriple = hasIcons && icons!.length >= 3;

    return (
      <LazyMotion features={domAnimation}>
        <motion.div
          ref={ref}
          initial="initial"
          animate="animate"
          whileHover="hover"
          className={cn(
            "group relative isolate flex flex-col items-center justify-center overflow-hidden rounded-2xl text-center transition-colors duration-300",
            SIZE_PADDING[size],
            variantClasses(variant),
            className,
          )}
        >
          {/* glow sutil no hover */}
          <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_60%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          {hasIcons && (
            <div className="mb-5 flex items-end justify-center">
              {isTriple ? (
                <div className="relative flex items-end justify-center gap-2">
                  <IconChip variant="left" className="z-0 -mr-3">
                    {icons![0]}
                  </IconChip>
                  <IconChip variant="center" className="z-10 h-14 w-14 [&>svg]:h-6 [&>svg]:w-6">
                    {icons![1]}
                  </IconChip>
                  <IconChip variant="right" className="z-0 -ml-3">
                    {icons![2]}
                  </IconChip>
                </div>
              ) : (
                <IconChip variant="center" className="h-14 w-14 [&>svg]:h-6 [&>svg]:w-6">
                  {icons![0]}
                </IconChip>
              )}
            </div>
          )}

          <motion.div variants={CONTENT_VARIANTS} className="max-w-sm">
            <h3
              className={cn(
                "font-display font-semibold tracking-tight text-foreground",
                TITLE_SIZE[size],
              )}
            >
              {title}
            </h3>
            {description && (
              <p
                className={cn(
                  "mt-2 leading-relaxed text-muted-foreground",
                  DESC_SIZE[size],
                )}
              >
                {description}
              </p>
            )}
          </motion.div>

          {(action || secondaryAction) && (
            <motion.div
              variants={BUTTON_VARIANTS}
              className="mt-6 flex flex-wrap items-center justify-center gap-2"
            >
              {action && <ActionButton action={action} primary />}
              {secondaryAction && <ActionButton action={secondaryAction} />}
            </motion.div>
          )}
        </motion.div>
      </LazyMotion>
    );
  },
);
EmptyState.displayName = "EmptyState";
